/**
 * IndividualPrecacheStatusContext
 * -------------------------------
 * Polling COMPARTIDO del endpoint /admin/cache/estado-individual.
 * 
 * Motivación: si N cards del listado admin hicieran polling individual del
 * endpoint /progress-doc/{id}, generaríamos N requests por ciclo → 429 seguros.
 * 
 * Solución: un solo Provider hace 1 request cada 8s y expone un getter por
 * manual_id. Los <PrecacheButtonWithBadge/> se suscriben al context.
 * 
 * Uso:
 *   <IndividualPrecacheStatusProvider>
 *     ...
 *     <PrecacheButtonWithBadge manualId={doc.id} .../>  // usa el context
 *   </IndividualPrecacheStatusProvider>
 * 
 * Fuera del provider (ej: en el visor con 1 solo doc), el componente hace
 * fallback a polling individual.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import api from '../services/api';

const IndividualPrecacheStatusContext = createContext(null);

// Polling adaptativo: si hay jobs activos, cada 6s; si no, cada 30s.
const POLL_MS_ACTIVE = 6000;
const POLL_MS_IDLE = 30000;

export function IndividualPrecacheStatusProvider({ children, enabled = true }) {
  const [state, setState] = useState({
    active_jobs: [],   // jobs pending/paused/processing_*
    recent_jobs: [],   // últimos 50 jobs (para badges done/failed)
    queue_size: 0,
    batch_running: false,
    loaded: false,
  });
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchOnce = useCallback(async () => {
    try {
      const res = await api.get('/documentos/admin/cache/estado-individual');
      if (!mountedRef.current) return;
      const data = res.data || {};
      setState({
        active_jobs: data.active_jobs || [],
        recent_jobs: data.recent_jobs || [],
        queue_size: data.queue_size || 0,
        batch_running: !!data.batch_running,
        loaded: true,
      });
    } catch (err) {
      // Silencioso: si el user no es admin o hay 401/403, no seguimos poleando.
      // Si es 429, dejamos que el próximo tick reintente.
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) return () => { mountedRef.current = false; };
    fetchOnce();
    const schedule = () => {
      if (!mountedRef.current) return;
      const hasActive = state.active_jobs && state.active_jobs.length > 0;
      const delay = hasActive ? POLL_MS_ACTIVE : POLL_MS_IDLE;
      timerRef.current = setTimeout(async () => {
        await fetchOnce();
        schedule();
      }, delay);
    };
    schedule();
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, state.active_jobs.length, fetchOnce]);

  const triggerRefetch = useCallback(() => {
    // Fuerza un fetch inmediato (útil tras encolar un job)
    fetchOnce();
  }, [fetchOnce]);

  const getStatusForManual = useCallback((manualId) => {
    if (!manualId) return null;
    // Prioridad 1: job activo
    const active = state.active_jobs.find((j) => j.manual_id === manualId);
    if (active) {
      return {
        active: true,
        status: active.status,
        stage: active.stage,
        current_page: active.current_page || 0,
        total_pages: active.total_pages || 0,
        percent: active.percent || 0,
        queue_position: active.queue_position || null,
        error: active.error || null,
      };
    }
    // Prioridad 2: último job reciente (para "done"/"failed"/"cancelled")
    const last = state.recent_jobs.find((j) => j.manual_id === manualId);
    if (last) {
      return {
        active: false,
        status: last.status,
        finished_at: last.finished_at,
        error: last.error,
        last_page_low: last.last_page_low || 0,
        last_page_hd: last.last_page_hd || 0,
      };
    }
    return { active: false, status: 'idle' };
  }, [state]);

  const value = {
    getStatusForManual,
    triggerRefetch,
    loaded: state.loaded,
    queueSize: state.queue_size,
    batchRunning: state.batch_running,
  };

  return (
    <IndividualPrecacheStatusContext.Provider value={value}>
      {children}
    </IndividualPrecacheStatusContext.Provider>
  );
}

export function useIndividualPrecacheStatus() {
  return useContext(IndividualPrecacheStatusContext); // null si fuera del provider
}
